import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { downloadCsv } from "../lib/csv";

const numFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const pctFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 });
const C_CONFIRMED = "#2563eb";
const C_OTHER = "#cbd5e1";

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold" style={{ color: accent || "#0f172a" }}>{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

const pct = (v) => (v === null || v === undefined ? "—" : `${pctFmt.format(v)}%`);

export default function BuyerOrderAnalysisDetail() {
  const navigate = useNavigate();
  // the CEO widget hands its filter context over in the URL so the page opens on the same numbers
  const initial = useMemo(() => new URLSearchParams(window.location.search), []);
  const [fromDate, setFromDate] = useState(initial.get("from") || "");
  const [toDate, setToDate] = useState(initial.get("to") || "");
  const [consignee, setConsignee] = useState(initial.get("consignee") || "");
  const [country, setCountry] = useState(initial.get("country") || "");
  const [plant, setPlant] = useState(initial.get("plant") || "");
  const [buyerName, setBuyerName] = useState(initial.get("buyer") || ""); // CEO global "Buyer" (recorded buyer master name)
  const [statusCode, setStatusCode] = useState("");
  const [selected, setSelected] = useState(null); // { consignee_id, consignee } — which buyer's orders are listed
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [buyerSearch, setBuyerSearch] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const summarySeq = useRef(0);
  const detailSeq = useRef(0);

  const body = useMemo(
    () => ({ from_date: fromDate, to_date: toDate, consignee, country, plant, buyer: buyerName, status_code: statusCode }),
    [fromDate, toDate, consignee, country, plant, buyerName, statusCode]
  );

  const load = useCallback(async () => {
    const seq = ++summarySeq.current; setLoading(true); setError("");
    try { const r = await api.post("/ai/ceo-chat/buyer-order-counts", body); if (seq !== summarySeq.current) return; setSummary(r.data); }
    catch (err) { if (seq !== summarySeq.current) return; setError(formatErr(err)); setSummary(null); }
    finally { if (seq === summarySeq.current) setLoading(false); }
  }, [body]);
  const loadDetail = useCallback(async () => {
    const seq = ++detailSeq.current; setDetailLoading(true); setDetailError("");
    try {
      const r = await api.post("/ai/ceo-chat/buyer-order-counts/detail", { ...body, consignee_id: selected?.consignee_id ?? "", limit: 5000 });
      if (seq !== detailSeq.current) return; setDetail(r.data);
    } catch (err) { if (seq !== detailSeq.current) return; setDetailError(formatErr(err)); setDetail(null); }
    finally { if (seq === detailSeq.current) setDetailLoading(false); }
  }, [body, selected]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDetail(); }, [loadDetail]);
  // a buyer picked in the table no longer applies once the filters change away from it
  useEffect(() => { setSelected(null); }, [fromDate, toDate, consignee, country, plant, buyerName, statusCode]);
  // the CEO global plant filter passes the plant code; show it as the plant name once the aliases arrive
  useEffect(() => {
    const alias = summary?.options?.plant_aliases?.[plant];
    if (alias && alias !== plant) setPlant(alias);
  }, [summary, plant]);

  const t = summary?.totals || {};
  const options = summary?.options || { consignees: [], countries: [], plants: [] };
  const buyers = useMemo(() => summary?.buyers || [], [summary]);
  const maxTotal = useMemo(() => Math.max(0, ...buyers.map((b) => b.total)), [buyers]);
  const filteredBuyers = useMemo(() => {
    const q = buyerSearch.trim().toLowerCase(); if (!q) return buyers;
    return buyers.filter((b) => [b.consignee, b.buyers, b.countries].join(" ").toLowerCase().includes(q));
  }, [buyers, buyerSearch]);
  const orders = useMemo(() => {
    const list = detail?.rows || []; const q = orderSearch.trim().toLowerCase(); if (!q) return list;
    return list.filter((r) => [r.pi_number, r.po_no, r.consignee, r.buyer, r.country, r.plants, r.status, r.processor].join(" ").toLowerCase().includes(q));
  }, [detail, orderSearch]);
  const confirmedName = summary?.confirmed_name || "Po Received";
  const dateFiltered = Boolean(fromDate || toDate);
  const hiddenUndated = t.undated_orders_hidden_by_date_filter || 0;

  const exportBuyers = useCallback(() => {
    downloadCsv("buyer-total-vs-confirmed-orders.csv",
      ["Buyer (consignee)", "Buyer Name", "Countries", "Total Orders", "Confirmed Orders", "Not Confirmed", "Confirmation %", "First Order", "Last Order"],
      filteredBuyers.map((b) => [b.consignee, b.buyers, b.countries, b.total, b.confirmed, b.not_confirmed, b.confirmation_pct ?? "", b.first_order, b.last_order]));
  }, [filteredBuyers]);
  const exportOrders = useCallback(() => {
    const tag = selected ? selected.consignee.replace(/[^a-z0-9]+/gi, "-").toLowerCase() : "all-buyers";
    downloadCsv(`buyer-orders-${tag}.csv`,
      ["Order No", "Order Date", "PO No", "PO Date", "Status", "Confirmed", "Buyer (consignee)", "Buyer Name", "Country", "Plant(s)", "Ship By", "Currency", "Processor", "Remarks"],
      orders.map((r) => [r.pi_number, r.order_date, r.po_no, r.po_date, r.status, r.confirmed ? "Yes" : "No", r.consignee, r.buyer, r.country, r.plants, r.ship_by_date, r.currency, r.processor, r.remarks]));
  }, [orders, selected]);

  const selectCls = "mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800";
  const labelCls = "text-xs font-semibold uppercase tracking-wide text-slate-500";
  const failClosed = summary && !summary.publishable && (t.orders || 0) > 0;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1500px] space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="max-w-3xl">
            <h1 className="text-xl font-semibold text-slate-900">Buyer Total Orders vs Confirmed Orders</h1>
            <p className="mt-1 text-sm text-slate-600">
              For each buyer, how many orders were raised and how many of them were confirmed. An order is confirmed once the
              ERP marks it “{confirmedName}” — the point at which the purchase order is recorded. Buyer is the consignee on
              the order, as on the ERP order list; where a separate buyer name is recorded it is shown alongside.
            </p>
          </div>
          <button type="button" onClick={() => navigate("/ceo-command-center?tab=po")}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Back to PO Portfolio</button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className={labelCls}>From Date<input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={selectCls} /></label>
            <label className={labelCls}>To Date<input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={selectCls} /></label>
            <label className={labelCls}>Buyer
              <select value={consignee} onChange={(e) => setConsignee(e.target.value)} className={`${selectCls} max-w-[240px]`}>
                <option value="">All Buyers</option>
                {consignee && !options.consignees.includes(consignee) ? <option value={consignee}>{consignee}</option> : null}
                {options.consignees.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            <label className={labelCls}>Country
              <select value={country} onChange={(e) => setCountry(e.target.value)} className={`${selectCls} max-w-[180px]`}>
                <option value="">All Countries</option>
                {country && !options.countries.includes(country) ? <option value={country}>{country}</option> : null}
                {options.countries.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            <label className={labelCls}>Plant
              <select value={plant} onChange={(e) => setPlant(e.target.value)} className={`${selectCls} max-w-[200px]`}>
                <option value="">All Plants</option>
                {plant && !options.plants.includes(plant) ? <option value={plant}>{plant}</option> : null}
                {options.plants.map((p) => <option key={p} value={p}>{p}</option>)}</select></label>
            <label className={labelCls}>Order Status
              <select value={statusCode} onChange={(e) => setStatusCode(e.target.value)} className={selectCls}>
                <option value="">All Orders</option>
                <option value="confirmed">Confirmed only</option>
                <option value="not_confirmed">Not confirmed only</option>
                {(summary?.status_master || []).map((s) => <option key={s.code} value={String(s.code)}>{s.name}</option>)}</select></label>
            <button type="button" onClick={() => { setFromDate(""); setToDate(""); setConsignee(""); setCountry(""); setPlant(""); setBuyerName(""); setStatusCode(""); }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Reset Filters</button>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Date basis: order (proforma invoice) date.{" "}
            {!dateFiltered ? "No date filter applied — every order ever raised is counted." : ""}{" "}
            {dateFiltered && hiddenUndated > 0 ? `${numFmt.format(hiddenUndated)} order(s) carry no order date in the ERP and are outside any date range (${numFmt.format(t.undated_confirmed_hidden_by_date_filter || 0)} of them confirmed).` : ""}
            {statusCode ? " The Order Status filter narrows the population, so Total Orders is the count at that status." : ""}
            {buyerName ? <span> Buyer name filter (from CEO filters): <strong>{buyerName}</strong> <button type="button" onClick={() => setBuyerName("")} className="text-blue-700 underline">clear</button></span> : null}
          </div>
        </div>

        {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</div> : null}
        {loading ? <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading…</div> : null}
        {!loading && failClosed ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="font-semibold">Validation check failed — figures withheld.</div>
            <div className="mt-1">{summary.calculation_status === "compatibility_failed"
              ? "The ERP data no longer matches the validated structure (unknown status code, unmapped buyer, or a duplicated order)."
              : "The buyer rows do not add up to the total in scope, or a confirmed order has no purchase-order record."}</div>
            <div className="mt-1 text-xs">{summary.reconciliation?.equation}</div>
          </div>
        ) : null}

        {!loading && summary && !failClosed ? (<>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatTile label="Total Orders" value={numFmt.format(t.orders || 0)} sub="Every order raised, any status" />
            <StatTile label="Confirmed Orders" value={numFmt.format(t.confirmed || 0)} sub={`ERP status “${confirmedName}”`} accent={C_CONFIRMED} />
            <StatTile label="Not Confirmed" value={numFmt.format(t.not_confirmed || 0)} sub="Pending, approved, cancelled or rejected" />
            <StatTile label="Confirmation Rate" value={pct(t.confirmation_pct)} sub="Confirmed ÷ total orders" />
            <StatTile label="Buyers" value={numFmt.format(t.buyers || 0)} sub={`${numFmt.format(t.buyers_with_confirmed || 0)} with a confirmed order`} />
          </div>

          {(summary.by_status || []).length ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">Orders by ERP status</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {summary.by_status.map((s) => (
                  <span key={s.code} className={`rounded-full border px-3 py-1 text-xs ${s.confirmed ? "border-blue-200 bg-blue-50 text-blue-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                    {s.status}: <span className="font-semibold">{numFmt.format(s.orders)}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Orders by buyer</div>
                <div className="mt-1 text-xs text-slate-500">
                  Blue = confirmed · grey = not confirmed. Bar length is the buyer's total orders. Click a buyer to list its orders below.
                  {summary.truncated ? ` Showing the first ${numFmt.format(buyers.length)} of ${numFmt.format(t.buyers || 0)} buyers — narrow the filters to see them all.` : ""}
                </div>
              </div>
              <div className="flex gap-2">
                <input value={buyerSearch} onChange={(e) => setBuyerSearch(e.target.value)} placeholder="Search buyer, country" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <button type="button" onClick={exportBuyers} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Export CSV</button>
              </div>
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">Buyer</th><th className="py-2 pr-3">Country</th><th className="py-2 pr-3 w-64">Confirmed vs total</th>
                  <th className="py-2 pr-3 text-right">Total Orders</th><th className="py-2 pr-3 text-right">Confirmed</th><th className="py-2 pr-3 text-right">Not Confirmed</th>
                  <th className="py-2 pr-3 text-right">Confirmation</th><th className="whitespace-nowrap py-2 pr-3">First Order</th><th className="whitespace-nowrap py-2 pr-3">Last Order</th><th className="py-2 pr-3"></th>
                </tr></thead>
                <tbody>{filteredBuyers.map((b) => {
                  const isSel = selected?.consignee_id === b.consignee_id;
                  return (
                    <tr key={b.consignee_id} onClick={() => setSelected(isSel ? null : { consignee_id: b.consignee_id, consignee: b.consignee })}
                      className={`cursor-pointer border-b border-slate-100 ${isSel ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                      <td className="py-2 pr-3 font-medium text-slate-800">
                        {b.consignee}
                        {b.buyers ? <span className="text-xs font-normal text-slate-500"> ({b.buyers})</span> : null}
                        {b.name_duplicated ? <span className="ml-1 rounded border border-amber-200 bg-amber-50 px-1 text-[10px] text-amber-800" title="More than one buyer master record carries this name; each record is shown separately.">separate master record</span> : null}
                      </td>
                      <td className="py-2 pr-3 text-slate-600">{b.countries || "—"}</td>
                      <td className="py-2 pr-3">
                        <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
                          <div style={{ width: `${maxTotal ? (b.confirmed / maxTotal) * 100 : 0}%`, backgroundColor: C_CONFIRMED }} />
                          <div style={{ width: `${maxTotal ? (b.not_confirmed / maxTotal) * 100 : 0}%`, backgroundColor: C_OTHER }} />
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right font-semibold text-slate-900">{numFmt.format(b.total)}</td>
                      <td className="py-2 pr-3 text-right" style={{ color: C_CONFIRMED }}>{numFmt.format(b.confirmed)}</td>
                      <td className="py-2 pr-3 text-right text-slate-700">{numFmt.format(b.not_confirmed)}</td>
                      <td className="py-2 pr-3 text-right text-slate-700">{pct(b.confirmation_pct)}</td>
                      <td className="whitespace-nowrap py-2 pr-3 text-slate-600">{b.first_order || "—"}</td>
                      <td className="whitespace-nowrap py-2 pr-3 text-slate-600">{b.last_order || "—"}</td>
                      <td className="whitespace-nowrap py-2 pr-3 text-right"><span className="text-xs font-medium text-blue-700">{isSel ? "Showing orders" : "View Orders"}</span></td>
                    </tr>
                  );
                })}</tbody>
              </table>
              {!filteredBuyers.length ? <div className="p-6 text-center text-sm text-slate-500">No buyers match.</div> : null}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Orders{selected ? ` — ${selected.consignee}` : " — all buyers in scope"}</div>
                <div className="mt-1 text-xs text-slate-500">
                  {detailLoading ? "Loading…" : detail?.truncated
                    ? `Showing ${numFmt.format(orders.length)} of ${numFmt.format(detail.matching_orders || 0)} orders — narrow the filters or pick a buyer to see them all`
                    : `${numFmt.format(orders.length)} order(s)${detail ? `, ${numFmt.format(detail.matching_confirmed || 0)} confirmed` : ""}`}
                  {selected ? <button type="button" onClick={() => setSelected(null)} className="ml-2 text-blue-700 underline">show all buyers</button> : null}
                </div>
              </div>
              <div className="flex gap-2">
                <input value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} placeholder="Search order, PO, buyer, plant, status" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <button type="button" onClick={exportOrders} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Export CSV</button>
              </div>
            </div>
            {detailLoading ? <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-6 text-center text-sm text-slate-500">Loading orders…</div> : null}
            {detailError ? <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">The order list could not be loaded: {detailError} The figures above are unaffected.</div> : null}
            <div className={`mt-3 overflow-x-auto${detailLoading || detailError ? " hidden" : ""}`}>
              <table className="w-full text-left text-sm">
                <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="whitespace-nowrap py-2 pr-3">Order No</th><th className="whitespace-nowrap py-2 pr-3">Order Date</th><th className="whitespace-nowrap py-2 pr-3">PO No</th><th className="whitespace-nowrap py-2 pr-3">PO Date</th>
                  <th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Confirmed</th><th className="py-2 pr-3">Buyer</th><th className="py-2 pr-3">Country</th>
                  <th className="py-2 pr-3">Plant(s)</th><th className="whitespace-nowrap py-2 pr-3">Ship By</th>
                </tr></thead>
                <tbody>{orders.slice(0, 300).map((r) => (
                  <tr key={r.order_id} className="border-b border-slate-100">
                    <td className="whitespace-nowrap py-2 pr-3 font-medium text-slate-800">{r.pi_number || "—"}</td>
                    <td className="whitespace-nowrap py-2 pr-3 text-slate-600">{r.order_date || "—"}</td>
                    <td className="whitespace-nowrap py-2 pr-3 text-slate-700">{r.po_no || "—"}</td>
                    <td className="whitespace-nowrap py-2 pr-3 text-slate-600">{r.po_date || "—"}</td>
                    <td className="whitespace-nowrap py-2 pr-3"><span className={`rounded-full border px-2 py-0.5 text-xs ${r.confirmed ? "border-blue-200 bg-blue-50 text-blue-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}>{r.status}</span></td>
                    <td className="py-2 pr-3 font-semibold text-slate-800">{r.confirmed ? "Yes" : "No"}</td>
                    <td className="py-2 pr-3 text-slate-700">{r.consignee}{r.buyer ? <span className="text-xs text-slate-500"> ({r.buyer})</span> : null}</td>
                    <td className="whitespace-nowrap py-2 pr-3 text-slate-600">{r.country || "—"}</td>
                    <td className="py-2 pr-3 text-slate-600">{r.plants || "—"}</td>
                    <td className="whitespace-nowrap py-2 pr-3 text-slate-600">{r.ship_by_date || "—"}</td>
                  </tr>))}</tbody>
              </table>
              {!orders.length ? <div className="p-6 text-center text-sm text-slate-500">No orders match.</div> : null}
              {orders.length > 300 ? <div className="mt-2 text-xs text-slate-500">Showing the first 300 rows on screen. Export CSV includes all {numFmt.format(orders.length)}.</div> : null}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-500 shadow-sm">
            <div className="font-semibold text-slate-700">How this is calculated</div>
            <div className="mt-1">{summary.validation_status}</div>
            <div className="mt-1">Grain: {summary.grain} Date basis: {summary.date_basis}.</div>
            <div className="mt-2">Totals reconcile: {summary.reconciliation?.ok ? "yes" : "no"} ({summary.reconciliation?.equation}).</div>
          </div>
        </>) : null}
      </div>
    </div>
  );
}
